'use client';
import classNames from 'classnames';
import { type ComponentPropsWithoutRef, type ElementType, forwardRef, memo, type ReactNode } from 'react';

import { type IconComponent, type IconSize } from '@air/design-icons';
import { button, buttonSpinner } from '@air/primitive-button';
import { Spinner } from '@air/primitive-spinner';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const iconButton = tailwindVariants({
  base: 'px-0',
  extend: button,
  variants: {
    size: {
      'extra-large': 'h-12 w-12 px-0 text-16',
      'extra-small': 'h-4 w-4 px-0 text-12',
      large: 'h-10 w-10 px-0 text-16',
      medium: 'h-8 w-8 px-0 text-14',
      small: 'h-6 w-6 px-0 text-12',
    },
  },
});

export const iconButtonSpinner = tailwindVariants({
  extend: buttonSpinner,
});

export const iconButtonTone = tailwindVariants({
  compoundVariants: [
    {
      appearance: 'ghost',
      class: 'ads:text-icon-primary disabled:ads:text-ads-gray-10',
      color: 'blue',
    },
    {
      appearance: 'outline',
      class: 'ads:text-icon-primary disabled:ads:text-primary',
      color: 'blue',
    },
    {
      appearance: 'ghost',
      class: 'ads:text-icon-error disabled:ads:text-ads-gray-10',
      color: 'red',
    },
    {
      appearance: 'outline',
      class: 'ads:text-icon-error disabled:ads:text-primary',
      color: 'red',
    },
  ],
  variants: {
    appearance: {
      filled: '',
      ghost: '',
      outline: '',
    },
    color: {
      blue: '',
      gray: '',
      grey: '',
      red: '',
      violet: '',
    },
  },
});

export const iconButtonIcon = tailwindVariants({
  base: 'block h-4 w-4',
  variants: {
    size: {
      'extra-large': 'h-6 w-6',
      'extra-small': 'h-3 w-3',
      large: 'h-5 w-5',
      medium: 'h-4 w-4',
      small: 'h-4 w-4',
    },
  },
});

export const ICON_BUTTON_ELEMENT_TAG = 'button';

export type IconButtonVariants = VariantProps<typeof iconButton>;

// iconButtonIcon's classes expressed as ADS size tokens: h-3 -> xs, h-4 -> sm, h-5 -> md, h-6 -> lg
export const ICON_BUTTON_ICON_SIZE: Record<NonNullable<IconButtonVariants['size']>, IconSize> = {
  'extra-large': 'lg',
  'extra-small': 'xs',
  large: 'md',
  medium: 'sm',
  small: 'xs',
};

export type IconButtonProps = Omit<
  ComponentPropsWithoutRef<typeof ICON_BUTTON_ELEMENT_TAG>,
  'children' | keyof IconButtonVariants
> &
  IconButtonVariants & {
    isLoading?: boolean;
    label: string;
  } & (
    | {
        /** Temporary. Marks `icon` as an @air/design-icons component so it receives a size token
         *  instead of a CSS class. Removed once every call site has migrated. */
        adsIcon: true;
        icon: IconComponent;
      }
    | {
        adsIcon?: false;
        icon: ElementType;
      }
    | {
        children: ReactNode;
      }
  );

export const IconButton = memo(
  forwardRef<HTMLButtonElement, IconButtonProps>(
    (
      {
        appearance = 'filled',
        className,
        color = 'blue',
        isLoading,
        label,
        size = 'medium',
        type = 'button',
        ...restOfProps
      },
      forwardedRef,
    ) => {
      let childrenNode: ReactNode;

      // ads:text-icon-* is emitted after ads:text-*, so a caller's text color would lose on source order.
      const callerSetsTextColor =
        typeof className === 'string' && /(?:^|\s)(?:[\w[\]:/.-]*:)*!?text-(?!\d)/.test(className);
      const toneClassName =
        'children' in restOfProps || callerSetsTextColor ? undefined : iconButtonTone({ appearance, color });

      if ('children' in restOfProps) {
        childrenNode = restOfProps.children;
      } else if (restOfProps.adsIcon) {
        childrenNode = <restOfProps.icon size={ICON_BUTTON_ICON_SIZE[size]} />;
      } else {
        childrenNode = <restOfProps.icon className={iconButtonIcon({ size })} />;
      }

      const {
        icon: _icon,
        adsIcon: _adsIcon,
        ...buttonProps
      } = 'icon' in restOfProps ? restOfProps : { ...restOfProps, adsIcon: undefined, icon: undefined };

      return (
        <button
          className={classNames(
            iconButton({ appearance, class: classNames(toneClassName, className), color, size }),
            isLoading ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
          ref={forwardedRef}
          type={type}
          {...buttonProps}
        >
          {isLoading ? (
            <div aria-live="polite" role="status">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <Spinner className={iconButtonSpinner({ size })} />
              </div>
              <VisuallyHidden>Loading...</VisuallyHidden>
              <div aria-hidden className="invisible opacity-0">
                {childrenNode}
              </div>
            </div>
          ) : (
            <>
              {label ? <VisuallyHidden>{label}</VisuallyHidden> : label}
              {childrenNode}
            </>
          )}
        </button>
      );
    },
  ),
);

IconButton.displayName = 'IconButton';
